/* earth-globe-astro.js — pure astronomy + geometry helpers for /earth-globe.
 *
 * No Three.js dependency: returns plain numbers / {x,y,z} objects so it is
 * unit-testable and reusable by the 2D fallback. All angles are degrees at the
 * API boundary, radians internally. UTC in, geographic out.
 *
 * Sun model: NOAA "General Solar Position Calculations" (fractional-year Fourier
 * series) for solar declination + equation of time — sub-arcminute EoT, ~0.2°
 * declination, no Julian-date machinery needed. The subsolar point drives the
 * lighting, so the day/night terminator is astronomically correct for the given
 * UTC instant (not a faked spinning light). Seasons emerge from the ±23.44°
 * declination sweep, so the Earth axis can stay vertical and still be correct.
 *
 * Refs: gml.noaa.gov/grad/solcalc/solareqns.PDF, en.wikipedia.org/wiki/Subsolar_point,
 * aa.usno.navy.mil/faq/GAST (GMST), Meeus "Astronomical Algorithms" (Moon).
 */
(function () {
  'use strict';

  var D2R = Math.PI / 180, R2D = 180 / Math.PI;
  var EARTH_R_KM = 6371.0088;

  function wrap180(x) { x = ((x % 360) + 360) % 360; return x > 180 ? x - 360 : x; }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  // Day-of-year (1..366) and decimal UTC hour for a Date.
  function dayOfYearUTC(date) {
    var start = Date.UTC(date.getUTCFullYear(), 0, 0);
    return Math.floor((date.getTime() - start) / 86400000);
  }
  function isLeap(y) { return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0; }

  // NOAA fractional-year solar model → { decl (deg), eqTime (min) }.
  function solar(date) {
    var y = date.getUTCFullYear();
    var doy = dayOfYearUTC(date);
    var hour = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    var daysInYear = isLeap(y) ? 366 : 365;
    var g = (2 * Math.PI / daysInYear) * (doy - 1 + (hour - 12) / 24); // radians
    var eqTime = 229.18 * (0.000075
      + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g)
      - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
    var decl = 0.006918
      - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g)
      - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g)
      - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g); // radians
    return { decl: decl * R2D, eqTime: eqTime };
  }

  // Sunrise / sunset at a latitude for the date, in LOCAL APPARENT SOLAR minutes
  // (longitude-independent; 720 = solar noon). state: normal | polar_day | polar_night.
  // Uses the standard -0.833 deg sunrise altitude (refraction + solar radius).
  function sunRiseSetLocal(lat, date) {
    var decl = solar(date).decl * D2R, la = lat * D2R, alt0 = -0.833 * D2R;
    var denom = Math.cos(la) * Math.cos(decl);
    if (Math.abs(denom) < 1e-9) return { state: 'polar_day' };
    var cosH = (Math.sin(alt0) - Math.sin(la) * Math.sin(decl)) / denom;
    if (cosH > 1) return { state: 'polar_night' };
    if (cosH < -1) return { state: 'polar_day' };
    var H = Math.acos(cosH) * R2D;
    return { state: 'normal', rise: 720 - 4 * H, set: 720 + 4 * H };
  }

  // Geographic point where the Sun is directly overhead now → { lat, lon } deg.
  function subsolarPoint(date) {
    var s = solar(date);
    var hours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    var lon = -15 * (hours - 12 + s.eqTime / 60);
    return { lat: s.decl, lon: wrap180(lon) };
  }

  // Julian Date (UT) for GMST/Moon.
  function julian(date) { return date.getTime() / 86400000 + 2440587.5; }

  // Greenwich Mean Sidereal Time in degrees (IAU 1982 simplified).
  function gmstDeg(date) {
    var d = julian(date) - 2451545.0;
    return ((280.46061837 + 360.98564736629 * d) % 360 + 360) % 360;
  }

  // Low-precision Moon position (Meeus, leading terms) → equatorial + sub-lunar.
  function moon(date) {
    var d = julian(date) - 2451545.0;
    var L = 218.316 + 13.176396 * d;        // mean longitude
    var M = 134.963 + 13.064993 * d;        // mean anomaly
    var F = 93.272 + 13.229350 * d;         // argument of latitude
    var Ms = 357.529 + 0.98560028 * d;      // Sun mean anomaly
    var Ls = 280.459 + 0.98564736 * d;      // Sun mean longitude
    var lon = L + 6.289 * Math.sin(M * D2R);                 // ecliptic longitude
    var lat = 5.128 * Math.sin(F * D2R);                     // ecliptic latitude
    var eps = 23.439 - 0.0000004 * d;
    var lr = lon * D2R, br = lat * D2R, er = eps * D2R;
    var ra = Math.atan2(Math.sin(lr) * Math.cos(er) - Math.tan(br) * Math.sin(er), Math.cos(lr)) * R2D;
    var dec = Math.asin(Math.sin(br) * Math.cos(er) + Math.cos(br) * Math.sin(er) * Math.sin(lr)) * R2D;
    // Sun apparent ecliptic longitude (for elongation → phase)
    var sunLon = Ls + 1.915 * Math.sin(Ms * D2R) + 0.020 * Math.sin(2 * Ms * D2R);
    var elong = ((lon - sunLon) % 360 + 360) % 360;          // 0=new, 180=full
    var illum = (1 - Math.cos(elong * D2R)) / 2;
    var subLon = wrap180(ra - gmstDeg(date));
    return { ra: ra, dec: dec, subLat: dec, subLon: subLon, illum: illum, elong: elong };
  }

  // 8-bucket Moon phase key from elongation (0..360).
  function moonPhaseKey(elong) {
    var idx = Math.floor(((elong + 22.5) % 360) / 45);
    return ['phase_new', 'phase_waxing_crescent', 'phase_first_quarter', 'phase_waxing_gibbous',
      'phase_full', 'phase_waning_gibbous', 'phase_last_quarter', 'phase_waning_crescent'][idx];
  }

  // Geographic (lat,lon deg) → 3D position on a sphere of radius r.
  // Convention aligns markers with the standard equirectangular Earth texture on
  // a default THREE.SphereGeometry (prime meridian / Greenwich consistent).
  function latLonToVec3(lat, lon, r) {
    r = r || 1;
    var phi = (90 - lat) * D2R;       // polar angle from +Y (north pole)
    var theta = (lon + 180) * D2R;    // azimuth
    var s = Math.sin(phi);
    return { x: -r * s * Math.cos(theta), y: r * Math.cos(phi), z: r * s * Math.sin(theta) };
  }
  // Inverse: 3D unit-ish vector → { lat, lon } deg.
  function vec3ToLatLon(x, y, z) {
    var r = Math.sqrt(x * x + y * y + z * z) || 1;
    var lat = 90 - Math.acos(clamp(y / r, -1, 1)) * R2D;
    var lon = Math.atan2(z, -x) * R2D - 180;
    return { lat: lat, lon: wrap180(lon) };
  }

  // Great-circle distance (km) and initial bearing (deg).
  function haversineKm(lat1, lon1, lat2, lon2) {
    var dLat = (lat2 - lat1) * D2R, dLon = (lon2 - lon1) * D2R;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(lat1 * D2R) * Math.cos(lat2 * D2R) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return EARTH_R_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Sample N points along the great circle between two geographic points → [{lat,lon}].
  function greatCirclePoints(lat1, lon1, lat2, lon2, n) {
    n = n || 64;
    var f1 = lat1 * D2R, l1 = lon1 * D2R, f2 = lat2 * D2R, l2 = lon2 * D2R;
    var d = 2 * Math.asin(Math.sqrt(
      Math.sin((f2 - f1) / 2) * Math.sin((f2 - f1) / 2)
      + Math.cos(f1) * Math.cos(f2) * Math.sin((l2 - l1) / 2) * Math.sin((l2 - l1) / 2)));
    var out = [];
    if (d === 0) return [{ lat: lat1, lon: lon1 }, { lat: lat2, lon: lon2 }];
    for (var i = 0; i <= n; i++) {
      var f = i / n;
      var A = Math.sin((1 - f) * d) / Math.sin(d);
      var B = Math.sin(f * d) / Math.sin(d);
      var x = A * Math.cos(f1) * Math.cos(l1) + B * Math.cos(f2) * Math.cos(l2);
      var y = A * Math.cos(f1) * Math.sin(l1) + B * Math.cos(f2) * Math.sin(l2);
      var z = A * Math.sin(f1) + B * Math.sin(f2);
      out.push({ lat: Math.atan2(z, Math.sqrt(x * x + y * y)) * R2D, lon: Math.atan2(y, x) * R2D });
    }
    return out;
  }

  // ISS visibility footprint angular radius (deg) from altitude (km).
  function footprintRadiusDeg(altKm) {
    return Math.acos(EARTH_R_KM / (EARTH_R_KM + altKm)) * R2D;
  }
  // Circle of given angular radius (deg) around a geographic centre → [{lat,lon}].
  function smallCircle(centerLat, centerLon, radiusDeg, n) {
    n = n || 72;
    var out = [], clat = centerLat * D2R, clon = centerLon * D2R, rad = radiusDeg * D2R;
    for (var i = 0; i <= n; i++) {
      var brng = (i / n) * 2 * Math.PI;
      var lat = Math.asin(Math.sin(clat) * Math.cos(rad) + Math.cos(clat) * Math.sin(rad) * Math.cos(brng));
      var lon = clon + Math.atan2(Math.sin(brng) * Math.sin(rad) * Math.cos(clat),
        Math.cos(rad) - Math.sin(clat) * Math.sin(lat));
      out.push({ lat: lat * R2D, lon: wrap180(lon * R2D) });
    }
    return out;
  }

  /* ── TopoJSON (world-atlas) decode → country features in lon/lat ───────── */
  function topoDecodeArc(topo, i) {
    var arc = topo.arcs[i], sc = topo.transform.scale, tr = topo.transform.translate;
    var x = 0, y = 0, pts = [];
    for (var k = 0; k < arc.length; k++) {
      x += arc[k][0]; y += arc[k][1];
      pts.push([x * sc[0] + tr[0], y * sc[1] + tr[1]]);
    }
    return pts;
  }
  function topoRing(topo, cache, arcIdxList) {
    var coords = [];
    for (var j = 0; j < arcIdxList.length; j++) {
      var a = arcIdxList[j], rev = a < 0, idx = rev ? ~a : a;
      var arc = cache[idx] || (cache[idx] = topoDecodeArc(topo, idx));
      var seg = rev ? arc.slice().reverse() : arc;
      coords = coords.length ? coords.concat(seg.slice(1)) : coords.concat(seg);
    }
    return coords;
  }
  // Returns [{ id, name, polygons:[ [outerRing, hole...], ... ] }] with rings of [lon,lat].
  function topoCountries(topo, objName) {
    var obj = topo.objects[objName || 'countries'];
    var cache = {}, feats = [];
    for (var i = 0; i < obj.geometries.length; i++) {
      var g = obj.geometries[i], polys = [];
      if (g.type === 'Polygon') {
        polys.push(g.arcs.map(function (r) { return topoRing(topo, cache, r); }));
      } else if (g.type === 'MultiPolygon') {
        for (var p = 0; p < g.arcs.length; p++) {
          polys.push(g.arcs[p].map(function (r) { return topoRing(topo, cache, r); }));
        }
      } else { continue; }
      feats.push({ id: g.id, name: (g.properties && g.properties.name) || '', polygons: polys });
    }
    return feats;
  }
  // Ray-casting point-in-ring ([lon,lat] pairs). pt = [lon,lat].
  function pointInRing(pt, ring) {
    var inside = false, x = pt[0], y = pt[1];
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
  }
  // Which country feature contains [lon,lat]? Returns feature or null.
  function countryAt(feats, lon, lat) {
    var pt = [lon, lat];
    for (var i = 0; i < feats.length; i++) {
      var polys = feats[i].polygons;
      for (var p = 0; p < polys.length; p++) {
        var rings = polys[p];
        if (rings.length && pointInRing(pt, rings[0])) {
          var inHole = false;
          for (var h = 1; h < rings.length; h++) { if (pointInRing(pt, rings[h])) { inHole = true; break; } }
          if (!inHole) return feats[i];
        }
      }
    }
    return null;
  }

  /* ISO 3166-1 numeric → alpha-2 (world-atlas ids are numeric). Used with
     Intl.DisplayNames for localized country names across all 9 locales.
     Unmapped ids fall back to the English properties.name. */
  var NUM2A2 = {
    4:'AF',8:'AL',12:'DZ',24:'AO',32:'AR',51:'AM',36:'AU',40:'AT',31:'AZ',44:'BS',50:'BD',112:'BY',56:'BE',
    84:'BZ',204:'BJ',64:'BT',68:'BO',70:'BA',72:'BW',76:'BR',96:'BN',100:'BG',854:'BF',108:'BI',116:'KH',
    120:'CM',124:'CA',140:'CF',148:'TD',152:'CL',156:'CN',170:'CO',178:'CG',180:'CD',188:'CR',384:'CI',
    191:'HR',192:'CU',196:'CY',203:'CZ',208:'DK',262:'DJ',214:'DO',218:'EC',818:'EG',222:'SV',226:'GQ',
    232:'ER',233:'EE',231:'ET',238:'FK',242:'FJ',246:'FI',250:'FR',254:'GF',266:'GA',270:'GM',268:'GE',
    276:'DE',288:'GH',300:'GR',304:'GL',320:'GT',324:'GN',624:'GW',328:'GY',332:'HT',340:'HN',348:'HU',
    352:'IS',356:'IN',360:'ID',364:'IR',368:'IQ',372:'IE',376:'IL',380:'IT',388:'JM',392:'JP',400:'JO',
    398:'KZ',404:'KE',408:'KP',410:'KR',414:'KW',417:'KG',418:'LA',428:'LV',422:'LB',426:'LS',430:'LR',
    434:'LY',440:'LT',442:'LU',807:'MK',450:'MG',454:'MW',458:'MY',466:'ML',478:'MR',484:'MX',498:'MD',
    496:'MN',499:'ME',504:'MA',508:'MZ',104:'MM',516:'NA',524:'NP',528:'NL',540:'NC',554:'NZ',558:'NI',
    562:'NE',566:'NG',578:'NO',512:'OM',586:'PK',591:'PA',598:'PG',600:'PY',604:'PE',608:'PH',616:'PL',
    620:'PT',630:'PR',634:'QA',642:'RO',643:'RU',646:'RW',682:'SA',686:'SN',688:'RS',694:'SL',706:'SO',
    710:'ZA',728:'SS',724:'ES',144:'LK',729:'SD',740:'SR',748:'SZ',752:'SE',756:'CH',760:'SY',158:'TW',
    762:'TJ',834:'TZ',764:'TH',626:'TL',768:'TG',780:'TT',788:'TN',792:'TR',795:'TM',800:'UG',804:'UA',
    784:'AE',826:'GB',840:'US',858:'UY',860:'UZ',548:'VU',862:'VE',704:'VN',887:'YE',894:'ZM',716:'ZW',
    10:'AQ',260:'TF',484:'MX',175:'YT',732:'EH',242:'FJ'
  };

  window.GlobeAstro = {
    D2R: D2R, R2D: R2D, EARTH_R_KM: EARTH_R_KM,
    wrap180: wrap180, clamp: clamp,
    solar: solar, subsolarPoint: subsolarPoint, gmstDeg: gmstDeg, sunRiseSetLocal: sunRiseSetLocal,
    moon: moon, moonPhaseKey: moonPhaseKey,
    latLonToVec3: latLonToVec3, vec3ToLatLon: vec3ToLatLon,
    haversineKm: haversineKm, greatCirclePoints: greatCirclePoints,
    footprintRadiusDeg: footprintRadiusDeg, smallCircle: smallCircle,
    topoCountries: topoCountries, countryAt: countryAt,
    num2alpha2: function (n) { return NUM2A2[n] || null; }
  };
})();
